import React, { useMemo, useCallback, useRef, useState } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  MarkerType,
  Panel,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import NarrativeCustomNode from './NarrativeCustomNode';
import { NODE_TYPES, NODE_STATUSES, EDGE_STYLES } from './narrativeConstants';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Plus, Eye, EyeOff, Search, X,
  GitBranch, Star, LayoutGrid, SlidersHorizontal, ChevronDown
} from 'lucide-react';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '../ui/popover';

const nodeTypes = { narrative: NarrativeCustomNode };

export default function NarrativeNodeGraph({
  narrativeNodes, narrativeEdges, language = 'fr', isMJ,
  onNodeClick, onEdgeClick, onAddNode, onConnect,
  onPositionsSave, selectedNodeId,
}) {
  const lang = language;
  const reactFlowWrapper = useRef(null);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterAct, setFilterAct] = useState('all');
  const [filterArc, setFilterArc] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [showMainOnly, setShowMainOnly] = useState(false);
  const [showSecrets, setShowSecrets] = useState(true);
  const [viewMode, setViewMode] = useState('full');
  const [showFilters, setShowFilters] = useState(false);

  const acts = useMemo(() => [...new Set(narrativeNodes.map(n => n.act).filter(Boolean))].sort(), [narrativeNodes]);
  const arcs = useMemo(() => [...new Set(narrativeNodes.map(n => n.arc).filter(Boolean))].sort(), [narrativeNodes]);

  const filteredNodeIds = useMemo(() => {
    return new Set(
      narrativeNodes
        .filter(n => {
          if (searchQuery) {
            const q = searchQuery.toLowerCase();
            if (!n.title.toLowerCase().includes(q) && !n.summary?.toLowerCase().includes(q) && !n.tags?.some(t => t.includes(q))) return false;
          }
          if (filterStatus !== 'all' && n.status !== filterStatus) return false;
          if (filterAct !== 'all' && n.act !== filterAct) return false;
          if (filterArc !== 'all' && n.arc !== filterArc) return false;
          if (filterType !== 'all' && n.node_type !== filterType) return false;
          if (showMainOnly && !n.is_main_plot) return false;
          if (!showSecrets && n.is_secret) return false;
          if (viewMode === 'accomplished' && !['accomplished', 'failed', 'avoided', 'modified'].includes(n.status)) return false;
          return true;
        })
        .map(n => n.id)
    );
  }, [narrativeNodes, searchQuery, filterStatus, filterAct, filterArc, filterType, showMainOnly, showSecrets, viewMode]);

  const outEdgeCounts = useMemo(() => {
    const counts = {};
    narrativeEdges.forEach(e => { counts[e.source_id] = (counts[e.source_id] || 0) + 1; });
    return counts;
  }, [narrativeEdges]);

  const rfNodes = useMemo(() => {
    return narrativeNodes
      .filter(n => filteredNodeIds.has(n.id))
      .map(n => ({
        id: n.id,
        type: 'narrative',
        position: { x: n.position_x || 0, y: n.position_y || 0 },
        selected: n.id === selectedNodeId,
        data: { ...n, language: lang, outEdgeCount: outEdgeCounts[n.id] || 0 },
      }));
  }, [narrativeNodes, filteredNodeIds, lang, outEdgeCounts, selectedNodeId]);

  const rfEdges = useMemo(() => {
    return narrativeEdges
      .filter(e => filteredNodeIds.has(e.source_id) && filteredNodeIds.has(e.target_id))
      .map(e => {
        const cfg = EDGE_STYLES[e.style] || EDGE_STYLES.default;
        const isActive = e.is_active !== false;
        const color = isActive ? cfg.stroke : '#6b7280';
        return {
          id: e.id,
          source: e.source_id,
          target: e.target_id,
          type: 'smoothstep',
          animated: e.style === 'main',
          label: e.label || undefined,
          labelStyle: { fill: color, fontSize: 9, fontWeight: 600 },
          labelBgStyle: { fill: 'var(--background)', fillOpacity: 0.85 },
          labelBgPadding: [4, 2],
          labelBgBorderRadius: 4,
          style: {
            stroke: color,
            strokeWidth: cfg.strokeWidth || 2,
            strokeDasharray: cfg.dashArray || (isActive ? undefined : '6 4'),
            opacity: isActive ? 1 : 0.5,
          },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color,
            width: 14,
            height: 14,
          },
          data: { ...e },
        };
      });
  }, [narrativeEdges, filteredNodeIds]);

  const [nodes, setNodes, onNodesChange] = useNodesState(rfNodes);
  // edges are fully controlled via props — do NOT pass onEdgesChange to ReactFlow
  // to prevent React Flow from overwriting our backend-synced edges
  const [edges, setEdges] = useEdgesState(rfEdges);

  React.useEffect(() => { setNodes(rfNodes); }, [rfNodes, setNodes]);
  React.useEffect(() => { setEdges(rfEdges); }, [rfEdges, setEdges]);

  const handleNodeClick = useCallback((_e, node) => { onNodeClick?.(node.id); }, [onNodeClick]);
  const handleEdgeClick = useCallback((_e, edge) => { onEdgeClick?.(edge.id); }, [onEdgeClick]);
  const handleConnect = useCallback((params) => { onConnect?.(params.source, params.target); }, [onConnect]);

  const handleNodeDragStop = useCallback((_e, _node, draggedNodes) => {
    const positions = draggedNodes.map(n => ({ id: n.id, x: Math.round(n.position.x), y: Math.round(n.position.y) }));
    onPositionsSave?.(positions);
  }, [onPositionsSave]);

  const activeFilterCount = [
    filterStatus !== 'all', filterAct !== 'all', filterArc !== 'all',
    filterType !== 'all', showMainOnly, !showSecrets, viewMode !== 'full',
  ].filter(Boolean).length;

  const clearFilters = () => {
    setSearchQuery(''); setFilterStatus('all'); setFilterAct('all');
    setFilterArc('all'); setFilterType('all'); setShowMainOnly(false);
    setShowSecrets(true); setViewMode('full');
  };

  return (
    <div ref={reactFlowWrapper} className="w-full h-full relative narrative-graph">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onNodeClick={handleNodeClick}
        onEdgeClick={handleEdgeClick}
        onConnect={handleConnect}
        onNodeDragStop={handleNodeDragStop}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.3 }}
        minZoom={0.05}
        maxZoom={2.5}
        snapToGrid
        snapGrid={[20, 20]}
        connectionLineStyle={{ stroke: 'hsl(var(--primary))', strokeWidth: 2, strokeDasharray: '6 3' }}
        proOptions={{ hideAttribution: true }}
      >
        <Background gap={20} size={1} color="hsl(var(--border) / 0.4)" />

        <Controls
          showInteractive={false}
          className="!bg-card/90 !backdrop-blur-xl !border !border-border/50 !shadow-xl !rounded-xl !overflow-hidden"
        />

        <MiniMap
          nodeColor={(node) => NODE_STATUSES[node.data?.status]?.color || '#94a3b8'}
          maskColor="hsl(var(--background) / 0.85)"
          className="!bg-card/90 !backdrop-blur-xl !border !border-border/50 !shadow-xl !rounded-xl"
          pannable
          zoomable
          style={{ width: 140, height: 90 }}
        />

        {/* ─── Toolbar ─── */}
        <Panel position="top-left" className="narrative-toolbar flex flex-col gap-2">
          {/* Primary bar: Search + Filters + Add */}
          <div className="flex items-center gap-1.5 flex-wrap">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
              <input
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder={lang === 'fr' ? 'Rechercher...' : 'Search...'}
                className="h-8 w-40 sm:w-48 pl-7 pr-7 text-xs rounded-lg bg-card/90 backdrop-blur-xl border border-border/50 shadow-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50 transition-all"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>

            {/* Filter toggle */}
            <Popover open={showFilters} onOpenChange={setShowFilters}>
              <PopoverTrigger asChild>
                <button
                  className={cn(
                    'h-8 px-2.5 rounded-lg text-xs font-medium flex items-center gap-1.5 transition-all border shadow-sm',
                    activeFilterCount > 0
                      ? 'bg-primary/10 border-primary/30 text-primary'
                      : 'bg-card/90 backdrop-blur-xl border-border/50 text-muted-foreground hover:text-foreground'
                  )}
                >
                  <SlidersHorizontal className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">{lang === 'fr' ? 'Filtres' : 'Filters'}</span>
                  {activeFilterCount > 0 && (
                    <span className="w-4 h-4 rounded-full bg-primary text-primary-foreground text-[9px] flex items-center justify-center font-bold">
                      {activeFilterCount}
                    </span>
                  )}
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-72 p-3 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold">{lang === 'fr' ? 'Filtres' : 'Filters'}</span>
                  {activeFilterCount > 0 && (
                    <button onClick={clearFilters} className="text-[10px] text-destructive hover:underline">
                      {lang === 'fr' ? 'Réinitialiser' : 'Reset'}
                    </button>
                  )}
                </div>

                {/* Status */}
                <div className="space-y-1">
                  <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">{lang === 'fr' ? 'Statut' : 'Status'}</label>
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{lang === 'fr' ? 'Tous' : 'All'}</SelectItem>
                      {Object.entries(NODE_STATUSES).map(([k, v]) => (
                        <SelectItem key={k} value={k}>
                          <span className="flex items-center gap-1.5">
                            <span className="w-2 h-2 rounded-full" style={{ background: v.color }} />
                            {v.label[lang]}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Type */}
                <div className="space-y-1">
                  <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Type</label>
                  <Select value={filterType} onValueChange={setFilterType}>
                    <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{lang === 'fr' ? 'Tous' : 'All'}</SelectItem>
                      {Object.entries(NODE_TYPES).map(([k, v]) => (
                        <SelectItem key={k} value={k}>
                          <span className="flex items-center gap-1.5">
                            <span className="w-2 h-2 rounded-full" style={{ background: v.color }} />
                            {v.label[lang]}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Act */}
                {acts.length > 0 && (
                  <div className="space-y-1">
                    <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">{lang === 'fr' ? 'Acte' : 'Act'}</label>
                    <Select value={filterAct} onValueChange={setFilterAct}>
                      <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{lang === 'fr' ? 'Tous' : 'All'}</SelectItem>
                        {acts.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Arc */}
                {arcs.length > 0 && (
                  <div className="space-y-1">
                    <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Arc</label>
                    <Select value={filterArc} onValueChange={setFilterArc}>
                      <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{lang === 'fr' ? 'Tous' : 'All'}</SelectItem>
                        {arcs.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Toggles */}
                <div className="flex flex-wrap gap-1.5 pt-1">
                  <button
                    onClick={() => setShowMainOnly(!showMainOnly)}
                    className={cn(
                      'text-[10px] font-medium px-2 py-1 rounded-md border transition-colors flex items-center gap-1',
                      showMainOnly ? 'bg-primary/10 border-primary/30 text-primary' : 'border-border text-muted-foreground hover:text-foreground'
                    )}
                  >
                    <Star className="h-3 w-3" /> {lang === 'fr' ? 'Principal' : 'Main'}
                  </button>
                  {isMJ && (
                    <button
                      onClick={() => setShowSecrets(!showSecrets)}
                      className={cn(
                        'text-[10px] font-medium px-2 py-1 rounded-md border transition-colors flex items-center gap-1',
                        !showSecrets ? 'bg-amber-500/10 border-amber-500/30 text-amber-600' : 'border-border text-muted-foreground hover:text-foreground'
                      )}
                    >
                      {showSecrets ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                      Secrets
                    </button>
                  )}
                </div>
              </PopoverContent>
            </Popover>

            {/* View mode pills */}
            <div className="hidden sm:flex items-center bg-card/90 backdrop-blur-xl border border-border/50 rounded-lg overflow-hidden shadow-sm">
              {[
                { key: 'full', label: lang === 'fr' ? 'Tout' : 'All', icon: LayoutGrid },
                { key: 'accomplished', label: lang === 'fr' ? 'Vécu' : 'Played', icon: GitBranch },
              ].map(mode => (
                <button
                  key={mode.key}
                  onClick={() => setViewMode(mode.key)}
                  className={cn(
                    'h-8 px-2.5 text-[10px] font-medium flex items-center gap-1 transition-colors',
                    viewMode === mode.key
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:text-foreground'
                  )}
                >
                  <mode.icon className="h-3 w-3" /> {mode.label}
                </button>
              ))}
            </div>

            {/* Add node */}
            {isMJ && (
              <button
                onClick={onAddNode}
                className="h-8 px-3 rounded-lg text-xs font-bold flex items-center gap-1.5 bg-primary text-primary-foreground hover:bg-primary/90 shadow-md shadow-primary/20 transition-all hover:shadow-lg hover:shadow-primary/30 active:scale-95"
              >
                <Plus className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">{lang === 'fr' ? 'Nœud' : 'Node'}</span>
              </button>
            )}
          </div>

          {/* Count badge */}
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-muted-foreground bg-card/80 backdrop-blur-sm border border-border/30 rounded-md px-2 py-0.5">
              {filteredNodeIds.size}/{narrativeNodes.length} {lang === 'fr' ? 'nœuds' : 'nodes'}
              {activeFilterCount > 0 && (
                <> · <button onClick={clearFilters} className="text-primary hover:underline">{lang === 'fr' ? 'tout voir' : 'show all'}</button></>
              )}
            </span>
          </div>
        </Panel>

        {/* ─── Legend ─── */}
        <Panel position="bottom-left">
          <Popover>
            <PopoverTrigger asChild>
              <button className="h-7 px-2.5 rounded-lg text-[10px] font-medium flex items-center gap-1.5 bg-card/90 backdrop-blur-xl border border-border/50 shadow-sm text-muted-foreground hover:text-foreground transition-colors">
                <span className="flex gap-0.5">
                  {['#22c55e', '#3b82f6', '#ef4444', '#f59e0b'].map(c => (
                    <span key={c} className="w-2 h-2 rounded-full" style={{ background: c }} />
                  ))}
                </span>
                {lang === 'fr' ? 'Légende' : 'Legend'}
                <ChevronDown className="h-3 w-3" />
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" side="top" className="w-64 p-3">
              <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2">
                {lang === 'fr' ? 'Statuts' : 'Statuses'}
              </p>
              <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
                {Object.entries(NODE_STATUSES).map(([k, v]) => (
                  <div key={k} className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full shrink-0 border border-white/10" style={{ background: v.color }} />
                    <span className="text-[10px] text-foreground/80">{v.label[lang]}</span>
                  </div>
                ))}
              </div>
              <div className="border-t border-border/50 mt-2.5 pt-2.5">
                <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2">
                  Types
                </p>
                <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 max-h-40 overflow-y-auto">
                  {Object.entries(NODE_TYPES).map(([k, v]) => {
                    const TypeIcon = v.icon;
                    return (
                      <div key={k} className="flex items-center gap-1.5">
                        <TypeIcon className="h-3 w-3 shrink-0" style={{ color: v.color }} />
                        <span className="text-[10px] text-foreground/80 truncate">{v.label[lang]}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </Panel>
      </ReactFlow>
    </div>
  );
}
